import { memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  InvoiceProcessingLoadingSteps,
  InvoiceProcessingStatus,
} from "@/lib/constants/invoiceStatus";
import { gql, useFragment } from "@apollo/client";

const INVOICE_FRAGMENT = gql`
  fragment InvoiceFields on Invoice {
    invoiceId
    processingStatus
  }
`;

type Props = {
  invoiceId: string;
};

export const ProcessingStates = memo(({ invoiceId }: Props) => {
  const { data } = useFragment({
    fragment: INVOICE_FRAGMENT,
    from: {
      __typename: "Invoice",
      invoiceId,
    },
  });

  const _processingStatus =
    data?.processingStatus ?? InvoiceProcessingStatus.UPLOADED;

  const [animate, setAnimate] = useState(false);
  const { t } = useTranslation();
  const states = [
    t("invoice_processing.extracting"),
    t("invoice_processing.classifying"),
    t("invoice_processing.accounting"),
  ];

  const index = InvoiceProcessingLoadingSteps.findIndex((steps) =>
    steps.includes(_processingStatus),
  );
  const [previousIndex, setPreviousIndex] = useState(index);

  useEffect(() => {
    if (index !== previousIndex) {
      setAnimate(true);

      setTimeout(() => {
        setAnimate(false);
        setPreviousIndex(index);
      }, 1000);
    }
  }, [index, previousIndex]);

  return (
    <div className="relative h-9 overflow-hidden">
      <div
        className={`absolute inset-0 flex items-center transform transition-transform duration-1000 ${
          animate ? "-translate-y-full" : "translate-y-0"
        }`}
        key={`${previousIndex}-${invoiceId}`}
      >
        <p className="text-muted-foreground">{states[previousIndex]}</p>
      </div>
      <div
        className={`absolute inset-0 flex items-center transform transition-transform duration-1000 ${
          animate ? "translate-y-0" : "translate-y-full"
        }`}
        key={`${index}-${invoiceId}`}
      >
        <p className="text-muted-foreground">{states[index]}</p>
      </div>
    </div>
  );
});
